import React from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  TrendingUp, Compass, Users, CheckCircle2, X as XIcon,
  Lightbulb, Clock, BarChart3, Play,
} from "lucide-react";

/**
 * Growth Mechanics — the Trend Agent's weekly insights view.
 *
 * Runs automatically every Monday at 09:00 UTC. Also triggerable on demand.
 * Each proposal is a small plain-English observation with a Tier A decision:
 * approve (acknowledge + make it a standing preference) or dismiss.
 */

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB",
    { day:"numeric", month:"short", year:"numeric" }); }
  catch { return iso; }
}

const ACTION_LABEL = {
  informational: "Informational",
  rule_suggestion: "Rule suggestion",
};

const CONF_COLOR = {
  high: "var(--elle-moss)",
  medium: "var(--elle-amber)",
  low: "var(--elle-ink-faint)",
};

export default function Growth() {
  const [latest, setLatest] = React.useState(null);
  const [running, setRunning] = React.useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/growth/latest");
      setLatest(data);
    } catch {
      toast.error("Couldn't load growth data.");
    }
  };
  React.useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/growth/run-now");
      setLatest(data);
      toast.success(`Generated ${data.proposals?.length || 0} proposal(s).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed.");
    } finally {
      setRunning(false);
    }
  };

  const decide = async (pid, decision) => {
    try {
      await api.post(`/growth/proposals/${pid}/decide`, { decision });
      toast.success(decision === "approved" ? "Approved." : "Dismissed.");
      await load();
    } catch {
      toast.error("Couldn't record decision.");
    }
  };

  if (!latest) {
    return <p className="font-mono-elle text-sm" style={{ color: "var(--elle-ink-dim)" }}>
      Loading…
    </p>;
  }

  const insights = latest.insights || {};
  const proposals = latest.proposals || [];
  const openProps = proposals.filter(p => p.status === "open");
  const decidedProps = proposals.filter(p => p.status !== "open");

  return (
    <div className="max-w-4xl" data-testid="growth-page">
      <div className="overline flex items-center gap-2">
        <TrendingUp size={12}/> Growth Mechanics
      </div>
      <h1 className="font-display text-5xl mt-2" style={{ color: "var(--elle-ink)" }}>
        What's working.
      </h1>
      <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--elle-ink-dim)" }}>
        The Trend Agent looks at the last four weeks of activity across
        Bluesky, Substack, and your newsletter. It surfaces patterns and
        sometimes suggests a rule change — which you approve or dismiss.
        Runs automatically every Monday at 09:00 UTC.
      </p>

      <div className="flex flex-wrap items-center gap-3 mt-6">
        <Button onClick={runNow} disabled={running}
                variant="outline" className="btn-ghost rounded-sm"
                data-testid="growth-run-now">
          <Play size={14} className="mr-2"/>
          {running ? "Computing…" : "Refresh now"}
        </Button>
        {latest.generated_at && (
          <span className="text-xs font-mono-elle" style={{ color: "var(--elle-ink-faint)" }}>
            Last refreshed {fmtDate(latest.generated_at)}
          </span>
        )}
      </div>

      {/* ========= Top-line numbers ========= */}
      <div className="grid md:grid-cols-3 gap-4 mt-10" data-testid="growth-insights-cards">
        {/* Subscribers */}
        <div className="elle-surface p-5 rounded-sm">
          <div className="overline flex items-center gap-2"><Users size={12}/> Substack</div>
          {insights.subscribers?.has_data ? (
            <>
              <div className="font-display text-4xl mt-2" style={{ color: "var(--elle-ink)" }}>
                {insights.subscribers.current}
              </div>
              <div className="text-xs font-mono-elle mt-1"
                   style={{ color: (insights.subscribers.gained_this_week || 0) >= 0
                                   ? "var(--elle-moss)" : "var(--elle-oxblood)" }}>
                {insights.subscribers.gained_this_week > 0 ? "+" : ""}
                {insights.subscribers.gained_this_week} this week
                {" · "}
                {insights.subscribers.gained_prior_week > 0 ? "+" : ""}
                {insights.subscribers.gained_prior_week} prior
              </div>
            </>
          ) : (
            <p className="text-sm mt-2" style={{ color: "var(--elle-ink-faint)" }}>
              No subscriber logs yet. Start logging on the Substack page.
            </p>
          )}
        </div>

        {/* Proactive conversion */}
        <div className="elle-surface p-5 rounded-sm">
          <div className="overline flex items-center gap-2"><Compass size={12}/> Proactive conversion</div>
          {(insights.proactive_conversion?.sent || 0) > 0 ? (
            <>
              <div className="font-display text-4xl mt-2" style={{ color: "var(--elle-ink)" }}>
                {Math.round((insights.proactive_conversion.rate || 0) * 100)}%
              </div>
              <div className="text-xs font-mono-elle mt-1" style={{ color: "var(--elle-ink-dim)" }}>
                {insights.proactive_conversion.conversions} of {insights.proactive_conversion.sent} openers got a reply
              </div>
            </>
          ) : (
            <p className="text-sm mt-2" style={{ color: "var(--elle-ink-faint)" }}>
              No proactive openers sent yet.
            </p>
          )}
        </div>

        {/* Best posting time */}
        <div className="elle-surface p-5 rounded-sm">
          <div className="overline flex items-center gap-2"><Clock size={12}/> Best hour to post</div>
          {insights.best_posting_hours_utc?.length ? (
            <>
              <div className="font-display text-4xl mt-2" style={{ color: "var(--elle-ink)" }}>
                {String(insights.best_posting_hours_utc[0].hour_utc).padStart(2, "0")}:00
              </div>
              <div className="text-xs font-mono-elle mt-1" style={{ color: "var(--elle-ink-dim)" }}>
                UTC · {insights.best_posting_hours_utc[0].reply_count} replies tracked
              </div>
            </>
          ) : (
            <p className="text-sm mt-2" style={{ color: "var(--elle-ink-faint)" }}>
              Need 4+ weeks of posts for a signal.
            </p>
          )}
        </div>
      </div>

      {/* ========= Top hashtags ========= */}
      {(insights.top_reply_topics?.length || 0) > 0 && (
        <div className="elle-surface p-5 rounded-sm mt-4">
          <div className="overline flex items-center gap-2">
            <BarChart3 size={12}/> Hashtags driving most conversation (last 28 days)
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {insights.top_reply_topics.map(t => (
              <span key={t.hashtag}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm font-mono-elle"
                    style={{ background: "var(--elle-surface-2)",
                             border: "1px solid var(--elle-line)",
                             color: "var(--elle-ink)" }}>
                <span style={{ color: "var(--elle-amber)" }}>{t.hashtag}</span>
                <span style={{ color: "var(--elle-ink-faint)" }}>· {t.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ========= Open proposals ========= */}
      <h2 className="font-display text-3xl mt-10 mb-4" style={{ color: "var(--elle-ink)" }}>
        Open proposals
      </h2>

      {openProps.length === 0 ? (
        <div className="elle-surface p-8 rounded-sm text-center" data-testid="growth-empty">
          <Lightbulb size={28} className="mx-auto mb-3" style={{ color: "var(--elle-ink-faint)" }}/>
          <p style={{ color: "var(--elle-ink-dim)" }}>
            Nothing to decide right now. The agent re-scans every Monday.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {openProps.map(p => (
            <div key={p.id} className="elle-surface p-5 rounded-sm"
                 data-testid={`growth-proposal-${p.id}`}
                 style={{ borderLeft: "3px solid var(--elle-amber)" }}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-mono-elle flex items-center gap-3 flex-wrap">
                    <span style={{ color: "var(--elle-amber)" }}>{ACTION_LABEL[p.action] || p.action}</span>
                    <span style={{ color: CONF_COLOR[p.confidence] || "var(--elle-ink-faint)" }}>
                      · confidence: {p.confidence}
                    </span>
                  </div>
                  <div className="font-display text-xl mt-2" style={{ color: "var(--elle-ink)" }}>
                    {p.headline}
                  </div>
                  <p className="text-sm mt-2" style={{ color: "var(--elle-ink-dim)" }}>
                    {p.body}
                  </p>
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <Button size="sm" className="btn-amber rounded-sm"
                        onClick={() => decide(p.id, "approved")}
                        data-testid={`growth-approve-${p.id}`}>
                  <CheckCircle2 size={14} className="mr-1"/>Approve
                </Button>
                <Button size="sm" variant="outline" className="btn-ghost rounded-sm"
                        onClick={() => decide(p.id, "dismissed")}
                        data-testid={`growth-dismiss-${p.id}`}>
                  <XIcon size={14} className="mr-1"/>Dismiss
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ========= Decided history ========= */}
      {decidedProps.length > 0 && (
        <div className="mt-10">
          <h3 className="font-display text-xl mb-3" style={{ color: "var(--elle-ink)" }}>
            Decided this week
          </h3>
          <div className="space-y-2">
            {decidedProps.map(p => (
              <div key={p.id} className="flex items-start gap-3 text-sm py-2 border-t"
                   style={{ borderColor: "var(--elle-line)" }}>
                {p.status === "approved"
                  ? <CheckCircle2 size={14} className="mt-0.5 shrink-0" style={{ color: "var(--elle-moss)" }}/>
                  : <XIcon size={14} className="mt-0.5 shrink-0" style={{ color: "var(--elle-ink-faint)" }}/>}
                <div className="flex-1">
                  <div style={{ color: "var(--elle-ink)" }}>{p.headline}</div>
                  <div className="text-xs font-mono-elle mt-1" style={{ color: "var(--elle-ink-faint)" }}>
                    {p.status} {p.decided_at ? `· ${fmtDate(p.decided_at)}` : ""}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
